import { Box, Link as MLink, Typography } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import * as api from '../api';
import { sendSmsForRegister } from '../api';
import loginValidationIcon from '../assets/icons/chattingIcon.svg';
import { LoginForm } from '../components/LoginForm';
import LoginLayout from '../components/LoginLayout';
import { RegisterForm } from '../components/RegisterForm';
import SvgIcon from '../components/SvgIcon';
import ValidationForm from '../components/ValidationForm';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { useAuth } from '../hooks/useAuth';
import { hiddenPhoneChars, sendToNative } from '../utils';

export function LoginPage() {
  const { setToken, token } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [screen, setScreen] = useState<'register' | 'validation'>('register');
  const { pathname } = location;
  const isRegisterPage = pathname === '/register';
  const [timeLeft, setTimeLeft] = useState(179); // 3 minutes
  const [registerSubmitting, setRegisterSubmitting] = useState(false);
  const showSnackbar = useAlertSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleSendSmsForRegister = async () => {
    try {
      const res = await sendSmsForRegister(
        phoneNumber.replace('+', ''),
        'MEMBER_CREATION',
        'SMS',
      );
      if (res.status === 200) {
        setScreen('validation');
        setTimeLeft(179);
      } else {
        const data = await res.json();
        throw data;
      }
    } catch (e: any) {
      showSnackbar(e.detail || t('loginPage.smsErrorMessage'), 'error');
    }
  };

  const handleSendWpForRegister = async () => {
    try {
      const res = await sendSmsForRegister(
        phoneNumber.replace('+', ''),
        'MEMBER_CREATION',
        'WA_TEMPLATE',
      );
      if (res.status === 200) {
        setScreen('validation');
        setTimeLeft(179);
      } else {
        const data = await res.json();
        throw data;
      }
    } catch (e: any) {
      showSnackbar(e.detail || t('loginPage.smsErrorMessage'), 'error');
    }
  };

  const handleUserRegister = async (otpCode: any) => {
    setRegisterSubmitting(true);
    try {
      const res = await api.userRegister(phoneNumber.replace('+', ''), otpCode);
      const data = await res.json();
      if (res?.status === 200) {
        setTimeout(() => {
          nav(
            `/new-account?phoneNumber=${phoneNumber.replace(
              '+',
              '',
            )}&newUserToken=${data.id_token}`,
          );
        }, 1000);
      } else {
        throw data;
      }
    } catch (e: any) {
      showSnackbar(e.detail || t('loginPage.registerErrorMessage'), 'error');
    } finally {
      setRegisterSubmitting(false);
    }
  };

  useEffect(() => {
    if (token) {
      // add a small delay to ensure the token is set
      setTimeout(() => {
        const next = searchParams.get('next');
        nav(next || '/account/orders');
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogin = async (password: string) => {
    try {
      setSubmitting(true);
      const res = await api.authenticate(
        phoneNumber.replace('+', ''),
        password,
      );
      const data = await res.json();

      if (res.status === 200) {
        localStorage.setItem('id_token', data.id_token);
        setToken(data.id_token);
        sendToNative('login', { jwt: data.id_token });
      } else {
        localStorage.removeItem('id_token');
        nav('/login');
        throw data;
      }
    } catch (e: any) {
      if (e.status === 400 || e.status === 401) {
        showSnackbar(
          e.detail || t('loginPage.loginErrorMessageCheckInfo'),
          'error',
        );
      } else {
        showSnackbar(e.detail || t('loginPage.loginErrorMessage'), 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoginLayout>
      {screen !== 'validation' ? (
        <>
          <Typography variant="h5" fontWeight="bold">
            {t('loginPage.title')}
          </Typography>
          <>
            <Typography
              mt="8px"
              variant="subtitle2"
              dangerouslySetInnerHTML={{ __html: t('loginPage.subtitle') }}
            />

            <Box
              p={0.5}
              color="secondary"
              sx={{
                borderRadius: '10px',
                display: 'flex',
                mt: 4,
                backgroundColor: '#EEE',
              }}
            >
              <ToggleButton
                value="list"
                onClick={() => nav('/login')}
                sx={{
                  fontSize: '16px',
                  textTransform: 'none',
                  width: '100%',
                  border: 'none',
                  background: !isRegisterPage ? '#fff' : 'inline',
                }}
              >
                {t('loginPage.loginButton')}
              </ToggleButton>

              <ToggleButton
                value="module"
                onClick={() => nav('/register')}
                sx={{
                  fontSize: '16px',
                  textTransform: 'none',
                  border: 'none',
                  width: '100%',
                  background: isRegisterPage ? '#fff' : 'inline',
                }}
              >
                {t('loginPage.registerButton')}
              </ToggleButton>
            </Box>
          </>

          <Box sx={{ maxWidth: '100%', mt: 4 }}>
            {isRegisterPage ? (
              <RegisterForm
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                onSmsSubmit={handleSendSmsForRegister}
                onWpSubmit={handleSendWpForRegister}
                submitting={registerSubmitting}
              />
            ) : (
              <LoginForm
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                onSubmit={handleLogin}
                submitting={submitting}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box mb={2} sx={{ textAlign: 'left' }}>
            {t(`loginPage.validationTitle`)}
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              textAlign: 'left',
              alignItems: 'start',
              mb: 4,
            }}
          >
            <SvgIcon
              src={loginValidationIcon}
              style={{ fill: 'currentColor', width: 36, marginTop: 4 }}
            />
            <Typography
              sx={{ fontSize: 14, ml: 2, mr: 4 }}
              dangerouslySetInnerHTML={{
                __html: t(`loginPage.validationInfo`, {
                  phoneNumber: hiddenPhoneChars(phoneNumber),
                }),
              }}
            />
          </Box>

          <ValidationForm
            onSubmit={handleUserRegister}
            retrySendSms={handleSendSmsForRegister}
            timeLeft={timeLeft}
            setTimeLeft={setTimeLeft}
          />
        </>
      )}

      <Box sx={{ pt: 2 }}>
        <MLink component={Link} to="/" color="text.primary">
          {t('loginPage.backToHome')}
        </MLink>
      </Box>
    </LoginLayout>
  );
}
